// THE BRAND PROFILE AND THE CHROME SEAM — one brand, named in one place.
//
// ---------------------------------------------------------------------------
// WHAT THIS IS, AND WHAT IT DELIBERATELY IS NOT
// ---------------------------------------------------------------------------
// Chrome and design tokens come from the UI-SDK — `@anshinus/*` for the Anshin/Anna leg,
// `@martletai/*` for the Martlet/Marti leg, NOT cross-wired — per Bryan's
// UI-standardisation ruling. They do NOT come from `@anshinus/anna-sdk-react`, which is
// why that package has no brand exports: not its job. This module does not implement
// chrome; it is the single place the brand is NAMED, so wiring the UI-SDK later is one
// edit rather than a hunt.
//
// IT IS NOT A `BRAND` SWITCH. There is no martlet branch and no conditional — the Marti
// side is a separate forked project, so a branch here would be a dead, unreachable path.
// What exists is ONE profile record and one attribute on `<html>`. The fork changes the
// record; no call site moves.
//
// ---------------------------------------------------------------------------
// THE AGENT NAME IS BRAND DATA — THE §1 CARVE-OUT
// ---------------------------------------------------------------------------
// Anna is the one SDK whose header name swaps per brand (every other SDK's is constant),
// so the agent name is a profile VALUE and never a literal in a component. The header
// reads `ANSHIN / Anna SDK Demo` here and `MARTLET / Marti SDK Demo` in the fork.
//
// The nav GROUP stays "Assistant" in BOTH brands — it names the domain, not the agent —
// so it lives in the page registry as a constant and is deliberately NOT derived from
// this profile. `pages.test.ts` pins that.
//
// ---------------------------------------------------------------------------
// WHAT THIS CANNOT REACH: THE SDK'S OWN STRINGS
// ---------------------------------------------------------------------------
// The published SDK renders its OWN agent name, and under 0.7.x that name is BRAND DATA
// baked into the artifact at publish time, not a literal this demo can touch. Measured in
// the installed `dist/index.mjs`: the launcher's label is `brand.labels.launchLabel` and
// the glyph resolves through `@anshinus/brand` by `brand.brandAppKey` — derived from the
// package's ACTIVE BRAND. `@anshinus/anna-sdk-react` ships active-brand=anshin, so those
// resolve to "Anna" / "anshin-anna"; `@martletai/marti-sdk-react` ships active-brand=
// martlet, so the same code resolves to "Marti". (Under 0.6.1 the label was a hardcoded
// literal `"Anna"` with no brand layer published at all — the layer was working-tree-only
// then; 0.7.x publishes it. See `../sdk-capabilities.ts`.)
//
// Either way, nothing in THIS demo parameterises those strings: the active brand is fixed
// per PACKAGE at publish, and this demo consumes the `@anshinus` package, so its launcher
// reads "Anna" — correct. "Do not hardcode the agent name" is honoured for every string
// this demo renders. The Marti leg gets its name by consuming `@martletai/marti-sdk-react`,
// NOT by pinning a brand on the `@anshinus` package in the hope the name follows the chrome.

/** The brand this build ships as. One constant; the fork changes it. */
export const BUILD_BRAND = 'anshin' as const;
export type BuildBrand = typeof BUILD_BRAND;

export interface BrandProfile {
  readonly id: string;
  /** Organisation label, as shown in the header lockup. */
  readonly orgName: string;
  /** The agent's name. Brand data — the §1 carve-out. */
  readonly agentName: string;
  /** npm scope this leg's UI-SDK and SDK come from. Never cross-wired. */
  readonly scope: string;
  /**
   * The React SDK coordinate this leg consumes.
   *
   * A FULL coordinate, not `${scope}/anna-sdk-react`: the Marti leg's package is
   * `@martletai/marti-sdk-react`, so the agent name is in the PACKAGE NAME too and
   * composing it from the scope would silently produce a name that does not exist.
   */
  readonly reactPackage: string;
}

export const BRAND_PROFILE: BrandProfile = Object.freeze({
  id: BUILD_BRAND,
  orgName: 'ANSHIN',
  agentName: 'Anna',
  scope: '@anshinus',
  reactPackage: '@anshinus/anna-sdk-react',
});

/** `Anna SDK Demo`. Derived, so the agent name has exactly one source. */
export function productName(p: BrandProfile = BRAND_PROFILE): string {
  return `${p.agentName} SDK Demo`;
}

/** `ANSHIN / Anna SDK Demo` — the header lockup. */
export function headerWordmark(p: BrandProfile = BRAND_PROFILE): string {
  return `${p.orgName} / ${productName(p)}`;
}

/**
 * Does the React package coordinate actually carry this brand's agent name?
 *
 * The guard against composing it. For the Anshin profile `${scope}/anna-sdk-react` and
 * the literal are the same string, so a composed version is INDISTINGUISHABLE here and a
 * mutation to it survives — the bug only appears in the fork, where composing yields
 * `@martletai/anna-sdk-react`, a package that does not exist. This states the invariant
 * so the fork is covered by a check rather than by whoever remembers.
 */
export function packageMatchesAgent(p: BrandProfile = BRAND_PROFILE): boolean {
  const name = p.reactPackage.split('/').pop() ?? '';
  return p.reactPackage.startsWith(`${p.scope}/`) && name.startsWith(`${p.agentName.toLowerCase()}-`);
}

/** The agent's name, for any sentence that needs it. */
export function agentName(p: BrandProfile = BRAND_PROFILE): string {
  return p.agentName;
}

/**
 * Substitute `{agent}` in a template.
 *
 * Page blurbs and capability labels are stored with the placeholder rather than the name,
 * so the registry stays static data while every rendered string is a function of the
 * profile. A test proves changing the profile changes the output — which is a behavioural
 * check, not a grep, and so cannot be satisfied by a comment.
 */
export function withAgent(template: string, p: BrandProfile = BRAND_PROFILE): string {
  return template.replace(/\{agent\}/g, p.agentName);
}

// ---------------------------------------------------------------------------
// Coherence
// ---------------------------------------------------------------------------

/**
 * The surfaces that must change together for a brand to be coherent.
 *
 * `name` is in the set because of the §1 carve-out: for this SDK the agent name swaps
 * with the chrome, so a build with Martlet tokens and the name "Anna" is exactly as
 * broken as one with an Anshin favicon. `favicon` is retained from the earlier set —
 * a superset is the safe direction.
 */
export const BRAND_SURFACES = ['stylesheet', 'name', 'logo', 'wordmark', 'favicon'] as const;
export type BrandSurface = (typeof BRAND_SURFACES)[number];

/**
 * Which brand each surface is currently serving.
 *
 * All serve `BUILD_BRAND` today because the UI-SDK package and import order are not yet
 * pinned (Infra is relaying them). Declared as a record rather than assumed, so when the
 * real token package lands the wiring updates entries here and `brandIsCoherent()`
 * catches a partial switch.
 */
export const BRAND_SURFACE_SOURCE: Readonly<Record<BrandSurface, BuildBrand>> = Object.freeze({
  stylesheet: BUILD_BRAND,
  name: BUILD_BRAND,
  logo: BUILD_BRAND,
  wordmark: BUILD_BRAND,
  favicon: BUILD_BRAND,
});

/**
 * Are all surfaces serving the same brand?
 *
 * Checks that the SET is uniform, not that it equals a particular brand — so the fork
 * gets the same guarantee without editing this function.
 */
export function brandIsCoherent(
  source: Readonly<Record<BrandSurface, string>> = BRAND_SURFACE_SOURCE,
): boolean {
  const seen = new Set(BRAND_SURFACES.map((s) => source[s]));
  return seen.size === 1;
}

/** Surfaces not serving `expected`. Empty when coherent. Named so a failure is actionable. */
export function mismatchedSurfaces(
  expected: string,
  source: Readonly<Record<BrandSurface, string>> = BRAND_SURFACE_SOURCE,
): BrandSurface[] {
  return BRAND_SURFACES.filter((s) => source[s] !== expected);
}

/**
 * Stamp the brand on `<html>` so CSS can scope tokens under `[data-brand="…"]` and the
 * UAT suite can assert it.
 *
 * `index.html` already carries the attribute statically, so the page is never briefly
 * unbranded before React runs. This re-applies it for the dev server and is idempotent.
 */
export function applyBrandAttribute(doc: Document = document): void {
  doc.documentElement.setAttribute('data-brand', BUILD_BRAND);
}
